import { defineStore } from "pinia";
import { instance } from "../index";
import { Material } from "@/models/material.model";
import { LearningObjective } from "@/models/learning-objective.model";
import { useLearningObjectiveStore } from "./learning-objective";
import { MaterialsRest } from "@/services/rest/materials.rest";
import { firstValueFrom, take, takeWhile } from "rxjs";
import { ELoader, useAdaptLoaderStore } from "./adapt-loader";
import { GenericsUtils } from "@/utils/generics.util";
import { useAlertStore } from "./alert";
import { StatusProgress, useAdaptProgressStore } from "./adapt-progress";
import { useAdaptChapterStore } from "./adapt-chapter";
import { useAdaptCourseStore } from "./adapt-course";
import { useAuthStore } from "../auth";
import { app } from "@/main";
import { useVideoStore } from "../video";
import { EventsRest } from "@/services/rest/events.rest";

export enum RouteMaterials {
  NEXT = "next",
  MORE = "more-materials",
  QUIZ = "quiz-materials",
}

export const store = defineStore({
  id: "adapt-material",
  state: () => ({
    materials: [] as Material[],
    materialsRest: new MaterialsRest(),
    eventsRest: new EventsRest(),
    indexMaterial: 0,
    learningObjective: {} as LearningObjective,
    genericsUtils: new GenericsUtils(),

    routeMaterial: RouteMaterials.NEXT,
    showMap: false,
    skipVideoModal: false,
  }),
  actions: {
    async setMaterials(courseId: string, userId: string): Promise<void> {
      try {
        useAdaptLoaderStore?.start(ELoader.MATERIALS);
        const params = {
          classroomId: localStorage.getItem("classId") ?? undefined,
        };
        const response = await new Promise((resolve, reject) => {
          this.materialsRest
            .getAllMaterials(courseId, userId, this.routeMaterial, params)
            .pipe(take(1))
            .subscribe({
              next: (data) => resolve(data),
              error: (error) => reject(error),
              complete: () => {
                useLearningObjectiveStore.hasFinished = false;
                useAdaptProgressStore?.setNextState();
              },
            });
        });
        this.handleMaterialsResponse(response);
      } catch (error) {
        this.handleMaterialsError(error);
        app.config.globalProperties.$router.go(-1);
      } finally {
        useAdaptLoaderStore?.stop(ELoader.MATERIALS);
      }
    },

    handleMaterialsResponse(response: any): void {
      useAdaptChapterStore?.setChapter(response.chapter);
      useAdaptCourseStore.course.done = response.done;
      useAdaptCourseStore.hasCertificate = response.hasCertificate;
      this.materials = response?.materials;
      useLearningObjectiveStore.setLearningObjective(
        response?.learningObjective
      );

      this.setCurrentMaterial();
    },

    handleMaterialsError(error: any): void {
      console.error("Error loading materials:", error);
    },

    setCurrentMaterial() {
      this.setCurrentIndex();
      this.formatMimeType();
    },
    setCurrentIndex() {
      if (this.allMaterialsDone) {
        this.indexMaterial = this.materials.length - 1;
        return;
      }
      const indexMaterialNotConcluded = this.materials.findIndex((material) => {
        return material.concluded == false;
      });
      this.indexMaterial =
        indexMaterialNotConcluded !== -1 ? indexMaterialNotConcluded : 0;
    },
    formatMimeType() {
      this.materials = this.materials.map((mat) => ({
        ...mat,
        file: {
          ...mat.file,
          url: this.genericsUtils.getSecureUrl(mat?.file?.url),
          mimetype: this.genericsUtils.getMimeType(mat?.file?.mimetype),
        },
      }));
    },

    async goNext() {
      if (this.isVideo && !this.getCurrentMaterial.concluded) {
        this.handleVideoMaterial();
      } else this.nextMaterial();
    },
    goBack() {
      this.indexMaterial = this.indexMaterial - 1;
    },
    async nextMaterial() {
      await this.checkAsConcluded();
      if (this.canGoToQuiz) {
        useLearningObjectiveStore.hasFinished = true;
        useAdaptProgressStore?.setNextState();
      } else {
        this.indexMaterial = this.indexMaterial + 1;
      }
    },
    async setMoreMaterial(route: RouteMaterials) {
      this.routeMaterial = route;
      await useAdaptMaterialStore?.setMaterials(
        useAdaptCourseStore?.course?.courseId,
        useAuthStore?.data?.id,
      );
      useAdaptProgressStore.status = StatusProgress.INIT_OBJECTIVE;
      useAlertStore.show = false;
    },

    setConcludedMaterialRequest() {
      const courseId = useAdaptCourseStore?.course.courseId;
      const userId = useAuthStore?.data.id;
      const data = {
        clientTime: new Date(),
        concluded: true,
        learningObjectiveId: useLearningObjectiveStore?.learningObjective.id,
        materialId: this.materials[this.indexMaterial].id,
        skipped: this.materials[this.indexMaterial].skipped,
        stoppedAt: this.materials[this.indexMaterial].stoppedAt ?? 0,
      };
      const classroomId = localStorage.getItem("classId") ?? undefined;
      return { courseId, userId, data, classroomId };
    },
    setSkippedMaterialRequest() {
      const courseId = useAdaptCourseStore?.course.courseId;
      const userId = useAuthStore?.data.id;
      const data = {
        clientTime: new Date(),
        concluded: true,
        learningObjectiveId: useLearningObjectiveStore?.learningObjective.id,
        materialId: this.materials[this.indexMaterial].id,
        skipped: true,
        stoppedAt: this.materials[this.indexMaterial]?.stoppedAt ?? 0,
      };
      const classroomId = localStorage.getItem("classId") ?? undefined;
      return { courseId, userId, data, classroomId };
    },

    async checkAsConcluded(): Promise<void> {
      const currentMaterial = this.getCurrentMaterial;

      if (currentMaterial.concluded) {
        return;
      }

      try {
        useAdaptLoaderStore?.start(ELoader.MATERIAL);

        const { courseId, userId, data, classroomId } = currentMaterial.skipped
          ? this.setSkippedMaterialRequest()
          : this.setConcludedMaterialRequest();

        await firstValueFrom(
          this.eventsRest?.addMaterialEvent(courseId, userId, data, {
            classroomId,
          })
        );

        this.materials[this.indexMaterial].concluded = data.concluded;
      } catch (error) {
        console.error("Error marking material as concluded:", error);
      } finally {
        useAdaptLoaderStore?.stop(ELoader.MATERIAL);
      }
    },
    onSkipVideo(confirm: boolean) {
      if (confirm) {
        this.materials[this.indexMaterial].skipped = true;
        this.materials[this.indexMaterial].stoppedAt =
          useVideoStore?.player?.cache_.currentTime;
        this.nextMaterial();
      }
      this.skipVideoModal = false;
    },
    handleVideoMaterial() {
      if (!useVideoStore?.getVideoEnded) {
        useVideoStore?.getPlayer?.pause();
        this.skipVideoModal = true;
      } else {
        this.nextMaterial();
      }
    },
    setShowMap(value: any): void {
      this.showMap = value;
    },
    onChooseMaterial(value: number) {
      this.indexMaterial = value;
    },
  },
  getters: {
    hasMaterial(): boolean {
      return this.materials?.length > 0;
    },
    hasStarted(): boolean {
      return this.materials[0]?.concluded === true;
    },
    isLastMaterial(): boolean {
      return this.indexMaterial === this.materials?.length - 1;
    },
    getTypeRouteMaterial() {
      return RouteMaterials[useLearningObjectiveStore?.getStatus];
    },
    getCurrentMaterial(): Material {
      return this.materials[this.indexMaterial];
    },
    isImage(): boolean {
      return (
        this.materials[this.indexMaterial]?.file?.mimetype == "png" ||
        this.materials[this.indexMaterial]?.file?.mimetype == "jpg" ||
        this.materials[this.indexMaterial]?.file?.mimetype == "jpeg" ||
        this.materials[this.indexMaterial]?.file?.mimetype == "svg"
      );
    },
    isVideo(): boolean {
      return (
        this.materials[this.indexMaterial]?.file?.mimetype == "mp4" ||
        this.materials[this.indexMaterial]?.file?.mimetype == "x-mpegURL"
      );
    },
    isAudio(): boolean {
      return this.materials[this.indexMaterial]?.file?.mimetype == "mp3";
    },
    isPdf(): boolean {
      return this.materials[this.indexMaterial]?.file?.mimetype == "pdf";
    },
    allPreviousMaterialsCompleted(): boolean {
      return this.materials.every((material, index) =>
        index < this.materials?.length - 1 ? material.concluded : true
      );
    },
    allMaterialsDone(): boolean {
      return this.materials?.every((e) => e.concluded);
    },
    canGoToQuiz(): boolean {
      if (!this.isLastMaterial) return false;
      return this.allPreviousMaterialsCompleted;
    },
  },
});

export const useAdaptMaterialStore = store(instance);
